import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/axiosInstance";
import { BedDouble, Users, Sparkle, ChevronRight } from "lucide-react";
import FloatingContactButton from "../components/FloatingContactButton";

const GuestRoomCatalog = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await api.get("/guest-rooms/get/all");
        setRooms(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-lime-50 flex items-center justify-center">
        <div className="inline-flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading luxury rooms...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-lime-50">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-r from-emerald-900 via-emerald-800 to-lime-800 text-white py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-lime-600/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkle className="w-5 h-5 text-emerald-300" />
            <span className="text-sm font-semibold tracking-widest text-emerald-200 uppercase">
              Luxury Accommodation
            </span>
            <Sparkle className="w-5 h-5 text-emerald-300" />
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Premium Guest Suites
          </h1>

          <p className="text-xl text-emerald-100 max-w-2xl">
            Experience unparalleled comfort and elegance in our meticulously
            designed guest rooms at RGCC. Choose a room type below to see
            details and request a booking.
          </p>
        </div>
      </div>

      {/* Room Cards */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {rooms.length === 0 ? (
          <div className="text-center py-16">
            <BedDouble className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No guest rooms available
            </h3>
            <p className="text-gray-500">Please check back later</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {rooms.map((room) => (
              <Link
                key={room.id}
                to={`/guest-room/${room.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-emerald-100 hover:-translate-y-1"
              >
                <div className="relative h-56 overflow-hidden">
                  {room.imageUrls?.length > 0 ? (
                    <img
                      src={room.imageUrls[0]}
                      alt={room.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-emerald-100 to-lime-100 flex items-center justify-center">
                      <BedDouble className="w-12 h-12 text-emerald-400" />
                    </div>
                  )}

                  <div className="absolute top-3 right-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold shadow ${
                        room.available
                          ? "bg-emerald-500 text-white"
                          : "bg-gray-500 text-white"
                      }`}
                    >
                      {room.available ? "Available" : "Unavailable"}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    {room.title}
                  </h3>

                  {room.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {room.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {room.capacity && (
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                          <Users className="w-4 h-4 text-emerald-500" />
                          Sleeps {room.capacity}
                        </span>
                      )}
                      {room.pricePerNight ? (
                        <span className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-lime-600 bg-clip-text text-transparent">
                          ৳{room.pricePerNight}
                          <span className="text-xs text-gray-500 font-normal">
                            {" "}
                            /night
                          </span>
                        </span>
                      ) : null}
                    </div>

                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 group-hover:gap-2 transition-all">
                      View
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <FloatingContactButton />
    </div>
  );
};

export default GuestRoomCatalog;
